import { useCallback, useMemo } from "react";
import { useCanManageOrganizationApi } from "@hooks/permissions/organization/useCanManageOrganizationApi";
import { useCanViewOrganizationActivityLog } from "@hooks/permissions/organization/useCanViewOrganizationActivityLog";
import { useCanViewOrganizationBilling } from "@hooks/permissions/organization/useCanViewOrganizationBilling";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    ORG_NAVIGATION_SHORTCUTS,
    OrgNavigationGate,
    OrgNavigationShortcutDefinition,
    OrgNavigationShortcutId,
} from "./orgNavigationShortcuts";
import { shortcutToKeys } from "./navigationShortcuts";
import { useOpenFirmSettings } from "./sideNav/useOpenFirmSettings";

export type ResolvedOrgNavigationShortcut = OrgNavigationShortcutDefinition & {
    keys: string;
    isEnabled: boolean;
};

export function useResolvedOrgNavigationShortcuts(): ResolvedOrgNavigationShortcut[] {
    const {
        closeManagement: isCloseManagementEnabled,
        reportTemplates: isReportTemplatesEnabled,
        organizationActivityLog: isActivityLogEnabled,
    } = useTypedFlags();
    const canViewActivityLog = useCanViewOrganizationActivityLog();
    const canViewBilling = useCanViewOrganizationBilling();
    const canManageApi = useCanManageOrganizationApi();
    const canOpenFirmSettings = useOpenFirmSettings() !== undefined;

    return useMemo(() => {
        const gateValues: Record<OrgNavigationGate, boolean> = {
            closeManagement: isCloseManagementEnabled,
            reportTemplates: isReportTemplatesEnabled,
            canViewActivityLog: isActivityLogEnabled && canViewActivityLog,
            canViewBilling,
            canManageApi,
            canOpenFirmSettings,
        };

        return ORG_NAVIGATION_SHORTCUTS.map((shortcut) => ({
            ...shortcut,
            keys: shortcutToKeys(shortcut.shortcut) ?? "",
            isEnabled: shortcut.gate ? gateValues[shortcut.gate] : true,
        }));
    }, [
        isCloseManagementEnabled,
        isReportTemplatesEnabled,
        isActivityLogEnabled,
        canViewActivityLog,
        canViewBilling,
        canManageApi,
        canOpenFirmSettings,
    ]);
}

/** The enabled org shortcut's hotkey string for a nav row, or `undefined`. */
export const useOrgNavigationShortcutKeys = () => {
    const shortcuts = useResolvedOrgNavigationShortcuts();

    return useCallback(
        (id: OrgNavigationShortcutId) =>
            shortcuts.find(
                (shortcut) => shortcut.isEnabled && shortcut.id === id,
            )?.keys,
        [shortcuts],
    );
};
